import { SELECT_ALL_VALUE } from "@/core/components/shared/data-table";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import {
  POT_TRANSACTION_TYPES,
  type PotTransactionType,
} from "@/features/fundraising/api/dto";
import { useFundraisingTranslation } from "@/features/fundraising/i18n/use-fundraising-translation";
import {
  TRANSACTION_STATUSES,
  type TransactionStatus,
} from "@/features/shared/api/enums";

export interface ContributionsFiltersValue {
  status?: TransactionStatus;
  /** Type de transaction cagnotte — défaut `POT_CONTRIBUTION`. */
  type?: PotTransactionType;
}

/**
 * Filtres du suivi des collectes — statut + type restreint aux types
 * cagnotte du wallet-service (`POT_CONTRIBUTION` / `POT_WITHDRAWAL`,
 * annexe A). Sentinelle `SELECT_ALL_VALUE` = « pas de filtre » (statut).
 */
export function ContributionsFilters({
  value,
  onChange,
}: {
  value: ContributionsFiltersValue;
  onChange: (value: ContributionsFiltersValue) => void;
}) {
  const { t } = useFundraisingTranslation();

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label>{t("fundraising.filters.type")}</Label>
        <Select
          value={value.type ?? "POT_CONTRIBUTION"}
          onValueChange={(v) =>
            onChange({ ...value, type: v as PotTransactionType })
          }
        >
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {POT_TRANSACTION_TYPES.map((type) => (
              <SelectItem key={type} value={type} className="notranslate">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>{t("fundraising.filters.status")}</Label>
        <Select
          value={value.status ?? SELECT_ALL_VALUE}
          onValueChange={(v) =>
            onChange({
              ...value,
              status: v === SELECT_ALL_VALUE ? undefined : (v as TransactionStatus),
            })
          }
        >
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL_VALUE}>
              {t("fundraising.filters.all")}
            </SelectItem>
            {TRANSACTION_STATUSES.map((status) => (
              <SelectItem key={status} value={status} className="notranslate">
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
